import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { CalendarClock, HardHat, Loader2, MapPinned, Play, RotateCcw, Settings2, Timer, X } from "lucide-react";
import { fmtD } from "../lib/date";
import { ReturnToDesignDialog } from "./ReturnToDesignDialog";

/**
 * 城市 → 工程小组 分配规则面板
 *
 * 需求来源：走查清单「二、重复冗余 #12」合并改造（2026-10-14）。
 *   背景：原先「待开工」在「工程项目」页有两处重复展示 —— 顶部 KickoffBanner 黄条横幅
 *   与下方「开工倒计时卡」（本文件），两者列同一批项目、按钮也重复。产品决定合并为
 *   只保留倒计时卡一处，故把横幅独有的「分配规则」入口一并搬到本文件。
 * 作用：管理层维护「城市 → 工程小组」的默认分配规则（手动规则优先，其余按历史项目多数小组）。
 * 权限：仅 admin / ceo / eng_manager 可见可改（由调用方 isMgr 控制是否渲染）。
 */
const TeamCityRules = ({ api, zh, onClose }) => {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/kickoff/team-cities").then(({ data: d }) => setData(d)).catch(() => setData({ cities: [], teams: [] })); }, [api]);
  const save = async () => {
    setBusy(true);
    try {
      const cities = {};
      (data.cities || []).forEach((c) => { if (c.source === "manual") cities[c.city] = c.team; });
      Object.entries(edits).forEach(([city, team]) => { if (team) cities[city] = team; else delete cities[city]; });
      const { data: r } = await api.put("/kickoff/team-cities", { cities });
      toast.success(zh ? `已保存 ${r.count} 条城市→小组规则` : `Saved ${r.count} rules`);
      setEdits({}); const { data: d } = await api.get("/kickoff/team-cities"); setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  if (!data) return <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline text-slate-300" /></div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="kickoff-rules">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-700 flex items-center gap-1"><MapPinned className="h-3.5 w-3.5 text-sky-600" />{zh ? "城市 → 工程小组 分配规则" : "City → team rules"}<span className="text-slate-400 font-normal ml-1">{zh ? `（${data.cities.length} 个城市 · 手动规则优先，其余按历史项目多数小组）` : ""}</span></p>
        <div className="flex items-center gap-2">
          {Object.keys(edits).length > 0 && <button data-testid="kickoff-rules-save" disabled={busy} onClick={save} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-900 text-white">{busy ? "…" : (zh ? "保存" : "Save")}</button>}
          <button data-testid="kickoff-rules-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
        {data.cities.map((c) => (
          <label key={c.city} className="flex items-center gap-2 text-[11px] text-slate-700 bg-slate-50 rounded-md px-2 py-1" data-testid={`kickoff-rule-${c.city.replace(/\s+/g, "_")}`}>
            <span className="flex-1 truncate">{c.city}<span className="text-slate-400 ml-1">{c.source === "manual" ? (zh ? "手动" : "manual") : `${c.count}`}</span></span>
            <select value={edits[c.city] ?? c.team} onChange={(e) => setEdits({ ...edits, [c.city]: e.target.value })} className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white">{data.teams.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}</select>
          </label>
        ))}
      </div>
    </div>
  );
};

/** 紧迫程度 → 卡片配色与状态标签（已开始 / 紧急·无动作 / 待开工 / 新分配） */
const urgency = (x, idleDays) => {
  if (x.status === "started") return { key: "started", cls: "border-emerald-200 bg-emerald-50/60", tag: "bg-emerald-600 text-white" };
  if (x.idle || x.days >= idleDays) return { key: "urgent", cls: "border-red-300 bg-red-50/70 shadow-[0_0_0_2px_rgba(239,68,68,.15)]", tag: "bg-red-600 text-white animate-pulse" };
  if (x.days >= 1) return { key: "warn", cls: "border-amber-300 bg-amber-50/70", tag: "bg-amber-500 text-white" };
  return { key: "fresh", cls: "border-sky-200 bg-sky-50/60", tag: "bg-sky-600 text-white" };
};

/** 计划开工日倒计时（负数=已过开工日；null=未定开工日） */
const Countdown = ({ x, zh }) => {
  if (x.countdown == null) return <p className="text-sm font-bold text-slate-400" data-testid={`kc-countdown-${x.id}`} data-days="">{zh ? "未定开工日" : "No start date"}</p>;
  const c = x.countdown;
  const cls = c < 0 ? "text-red-600" : c <= 3 ? "text-orange-600" : c <= 7 ? "text-amber-600" : "text-emerald-700";
  return (
    <p className={`font-bold leading-none ${cls}`} data-testid={`kc-countdown-${x.id}`} data-days={c}>
      <span className="text-2xl">{Math.abs(c)}</span><span className="text-xs ml-1">{c < 0 ? (zh ? "天 · 开工日已过" : "d overdue") : c === 0 ? (zh ? "今日开工" : "starts today") : (zh ? "天后开工" : "d to start")}</span>
    </p>
  );
};

/**
 * 工程小组看板 · 开工倒计时卡
 *
 * 2026-10-14 合并（走查清单「二、重复冗余 #12」）：
 *   本组件现在是「待开工」在「工程项目」页的**唯一**展示与操作入口 ——
 *   原顶部 KickoffBanner 黄条横幅（接口 /kickoff/pending）已删除，原因是它与本卡片
 *   重复展示同一批待开工项目、且「开始处理 / 退回设计部」按钮也重复。
 *   横幅原有的三项能力已并入本卡：
 *     ① 卡片头部「N 个新城市待分配小组」红色徽章（data-testid=kickoff-pending-team-count）
 *     ② 针对 status=pending_team 项目的「选择小组 + 分配」操作（仅 admin / ceo / eng_manager）
 *     ③ 「分配规则」入口（城市→小组，同上权限，展开 TeamCityRules 面板）
 *   数据接口仍为 GET /kickoff/cards（后端已补 pendingTeam / teams 两个字段）。
 */
export const KickoffCountdownCards = () => {
  const { api, user, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ret, setRet] = useState(null);        // 正在退回设计部的卡片（非空时弹出确认框）
  const [rules, setRules] = useState(false);   // 是否展开「分配规则」面板（合并自 KickoffBanner）
  const [pick, setPick] = useState({});        // 每个「待分配小组」项目当前选中的小组 key
  const load = () => api.get("/kickoff/cards").then(({ data: d }) => setData(d)).catch(() => setData({ items: [] }));
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line
  if (!data?.items?.length) return null;
  const start = async (id) => {
    setBusy(id);
    try { await api.post(`/kickoff/${id}/start`); toast.success(zh ? "已确认开始处理" : "Started"); await load(); window.dispatchEvent(new Event("kickoff-changed")); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };
  // 分配工程小组（合并自 KickoffBanner）：针对 status=pending_team 的新城市项目，
  // 仅 admin / ceo / eng_manager 可操作；remember=true 表示同时记住「城市→小组」规则。
  const assign = async (id) => {
    const team = pick[id];
    if (!team) return toast.error(zh ? "请选择小组" : "Pick a team");
    setBusy(id);
    try {
      const { data: r } = await api.post(`/kickoff/${id}/assign`, { team, remember: true });
      toast.success(zh ? `已分配给 ${r.teamName}，并记住城市「${r.city}」→${r.teamName}` : `Assigned to ${r.teamName}`);
      await load(); window.dispatchEvent(new Event("kickoff-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const pending = data.items.filter((x) => x.status !== "started");
  const isMgr = ["admin", "ceo", "eng_manager"].includes(user?.role);
  const isPmOf = (x) => !!user?.name && user.name === x.manager;
  // 已分配：小组成员/PM/工程经理均可开始；待分配小组：工程经理或该项目 PM 可直接开始（后端同步放行）
  const canStartNow = (x) => x.canStart && (x.status === "assigned" || (x.status === "pending_team" && (isMgr || isPmOf(x))));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="kickoff-cards" data-count={data.items.length}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-amber-600" />{zh ? "开工倒计时" : "Kickoff countdown"}</p>
        <span className="text-[11px] text-slate-500" data-testid="kickoff-cards-summary">{zh ? `${pending.length} 个待开工 · 超 ${data.idleDays} 天无动作将短信催促 CEO / 小组 / PM` : `${pending.length} pending · SMS escalation after ${data.idleDays}d idle`}</span>
        {/* 待分配小组数徽章（合并自 KickoffBanner 横幅头部）：仅管理层可见 */}
        {data.pendingTeam > 0 && isMgr && <span data-testid="kickoff-pending-team-count" className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white">{zh ? `${data.pendingTeam} 个新城市待分配小组` : `${data.pendingTeam} need team`}</span>}
        {/* 分配规则入口（合并自 KickoffBanner 横幅头部）：仅管理层可见 */}
        {isMgr && <button data-testid="kickoff-rules-btn" onClick={() => setRules(!rules)} className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-semibold text-sky-700 hover:underline"><Settings2 className="h-3 w-3" />{zh ? "分配规则" : "Rules"}</button>}
      </div>
      {rules && isMgr && <div className="mb-3"><TeamCityRules api={api} zh={zh} onClose={() => setRules(false)} /></div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
        {data.items.map((x) => {
          const u = urgency(x, data.idleDays);
          return (
            <div key={x.id} data-testid={`kc-card-${x.id}`} data-urgency={u.key} data-status={x.status} className={`rounded-xl border p-3 flex flex-col gap-2 ${u.cls}`}>
              <div className="flex items-start gap-2">
                <button data-testid={`kc-open-${x.id}`} onClick={() => nav(`/project/${x.id}`)} className="text-left text-sm font-semibold text-slate-800 hover:text-sky-700 leading-snug flex-1 min-w-0 truncate">{x.address}</button>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${u.tag}`} data-testid={`kc-status-${x.id}`}>
                  {x.status === "started" ? (zh ? "已开始" : "Started") : x.status === "pending_team" ? (zh ? "待分配小组" : "Needs team") : u.key === "urgent" ? (zh ? "紧急 · 无动作" : "Urgent") : u.key === "warn" ? (zh ? "待开工" : "Pending") : (zh ? "新分配" : "New")}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 truncate">{x.teamName || (zh ? "未分配" : "Unassigned")}{x.manager ? ` · PM ${x.manager}` : ""}{x.city ? ` · ${x.city}` : ""}</p>
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="text-[10px] text-slate-400">{x.status === "started" ? (zh ? "已开工" : "Started") : (zh ? "计划开工" : "Planned start")} {x.status === "started" ? (fmtD(x.startedAt) || "—") : (fmtD(x.startDate) || "—")}</p>
                  {x.status === "started"
                    ? <p className="text-sm font-bold text-emerald-700" data-testid={`kc-countdown-${x.id}`} data-days="">{zh ? "已开工 · 不再倒计时" : "Started · no countdown"}</p>
                    : <Countdown x={x} zh={zh} />}
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400 flex items-center justify-end gap-0.5"><Timer className="h-3 w-3" />{x.status === "started" ? (zh ? "已开始处理" : "Started") : (zh ? "分配后已停留" : "Since assigned")}</p>
                  {x.status === "started"
                    ? <p className="text-xs font-semibold text-emerald-700" data-testid={`kc-since-${x.id}`}>{fmtD(x.startedAt)}{x.startedBy ? ` · ${x.startedBy}` : ""}</p>
                    : <p className={`text-lg font-bold leading-none ${u.key === "urgent" ? "text-red-600" : u.key === "warn" ? "text-amber-600" : "text-sky-700"}`} data-testid={`kc-since-${x.id}`} data-days={x.days}>{x.days}<span className="text-[10px] ml-0.5">{zh ? "天" : "d"}</span></p>}
                </div>
              </div>
              {x.smsCount > 0 && <p className="text-[10px] text-red-600 font-semibold" data-testid={`kc-sms-${x.id}`}>{zh ? `已短信催促 ${x.smsCount} 次` : `SMS ×${x.smsCount}`}</p>}
              <div className="mt-auto flex items-center gap-2 flex-wrap">
                {/* 分配小组：新城市（pending_team）+ 管理层 —— 合并自 KickoffBanner 横幅 */}
                {x.status === "pending_team" && isMgr && (
                  <>
                    <select data-testid={`kickoff-team-select-${x.id}`} value={pick[x.id] || ""} onChange={(e) => setPick({ ...pick, [x.id]: e.target.value })} className="flex-1 min-w-[110px] text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"><option value="">{zh ? "选择小组…" : "Team…"}</option>{(data.teams || []).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}</select>
                    <button data-testid={`kickoff-assign-${x.id}`} disabled={busy === x.id} onClick={() => assign(x.id)} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-60">{busy === x.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "分配" : "Assign")}</button>
                  </>
                )}
                {canStartNow(x) && (
                  <button data-testid={`kc-start-${x.id}`} disabled={busy === x.id} onClick={() => start(x.id)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">{busy === x.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}{zh ? "开始处理" : "Start"}</button>
                )}
                {/* 退回设计部：仅未开工项目 + 工程经理/admin/CEO 可见；用于缺 Permit / 图纸未就绪无法开工时退回上一部门 */}
                {isMgr && x.status !== "started" && (
                  <button data-testid={`kc-return-${x.id}`} onClick={() => setRet(x)} title={zh ? "退回设计部（缺 Permit 等无法开工时）" : "Return to Design"} className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"><RotateCcw className="h-3.5 w-3.5" />{zh ? "退回设计部" : "Return"}</button>
                )}
              </div>
              {/* 非管理层看到的「待分配小组」提示（管理层上面已有分配下拉） */}
              {x.status === "pending_team" && !canStartNow(x) && !isMgr && <p className="text-[10px] text-red-600 flex items-center gap-1"><HardHat className="h-3 w-3" />{zh ? "新城市 · 待工程经理分配小组" : "New city · assign team"}</p>}
            </div>
          );
        })}
      </div>
      {ret && <ReturnToDesignDialog item={ret} onClose={() => setRet(null)} onDone={async () => { setRet(null); await load(); window.dispatchEvent(new Event("kickoff-changed")); }} />}
    </div>
  );
};
